import { AxiosResponse } from 'axios';

import { prepareFiltersOfSelection, prepareQPFromFilters, serverAxios } from '../../http';
import {
  ICreateRateRequestBody,
  ICreateRateResponse,
  IDeleteRatesFilters,
  IDeleteRatesResponse,
  IGetJobRatesResponse,
  IGetRatesFilters,
  IGetRatesResponse,
  IGetSingleRateResponse,
  IUpdateRateRequestBody,
  IUpdateRateResponse,
  IRatesSetModeResponse,
} from 'models/http/rate';
import { HttpTimeoutPriority } from 'utils/enums/http-timeout-priority';
import { HTTP } from './core.http';
import DataUploadBaseHttp from './utils/data-upload.http';
import { DataUploadModels, ModelsNames } from 'utils/enums/model-names';
import { IGetSimpleListFilters, IGetSimpleListResponse } from 'models/http/common';
import { IGetHistoryResponse } from 'models/http/history';

class RatesHttp extends DataUploadBaseHttp implements HTTP<IDeleteRatesFilters> {
  protected readonly route: DataUploadModels = ModelsNames.Rates;

  public async history(id: string, abortController?: AbortController): Promise<IGetHistoryResponse> {
    return serverAxios
      .get(`/${this.route}/${id}/history`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Highest,
      })
      .then((response: AxiosResponse<IGetHistoryResponse>) => response.data);
  }

  public async switchMode(id: string, isActive: boolean, abortController?: AbortController): Promise<number> {
    return serverAxios
      .put(
        `/${this.route}/${id}/mode`,
        {
          isActive,
        },
        {
          signal: abortController?.signal,
          timeout: HttpTimeoutPriority.Highest,
        },
      )
      .then((response: AxiosResponse<IRatesSetModeResponse>) => response.data.id);
  }

  public async getSimple(_?: IGetSimpleListFilters, abortController?: AbortController): Promise<IGetSimpleListResponse> {
    return serverAxios
      .get(`/${this.route}/simple`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetSimpleListResponse>) => {
        return response.data;
      });
  }

  public async getById(id: string, abortController?: AbortController): Promise<IGetSingleRateResponse> {
    return serverAxios
      .get(`/${this.route}/single/${id}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetSingleRateResponse>) => {
        return response.data;
      });
  }

  public async get(filters?: IGetRatesFilters, abortController?: AbortController): Promise<IGetRatesResponse['records']> {
    const queryParams = prepareQPFromFilters(filters || {});

    return serverAxios
      .get(`/${this.route}?${queryParams.toString()}`, {
        timeout: HttpTimeoutPriority.Medium,
        signal: abortController?.signal,
      })
      .then((response: AxiosResponse<IGetRatesResponse>) => {
        return response.data.records;
      });
  }

  public async create(data: ICreateRateRequestBody): Promise<ICreateRateResponse> {
    return serverAxios
      .post(`/${this.route}`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<ICreateRateResponse>) => {
        return response.data;
      });
  }

  public async getJobRates(job: Record<string, unknown>, abortController?: AbortController): Promise<unknown[]> {
    return serverAxios
      .post(
        this.route,
        {
          act: 'getjobrates',
          project: { code: 1, service: 1, value: 1, addition: 1, total: 1 },
          job,
          additionsOnly: true,
        },
        {
          signal: abortController?.signal,
          timeout: HttpTimeoutPriority.Medium,
        },
      )
      .then((response: AxiosResponse<IGetJobRatesResponse>) => {
        return response.data.rates;
      });
  }

  public async update(id: number, data: IUpdateRateRequestBody): Promise<IUpdateRateResponse> {
    return serverAxios
      .put(`/${this.route}/${id}`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IUpdateRateResponse>) => {
        return response.data;
      });
  }

  public async delete(ids: number[]): Promise<IDeleteRatesResponse>;
  public async delete(filters: IDeleteRatesResponse): Promise<IDeleteRatesResponse>;
  public async delete(filters: number[] | IDeleteRatesResponse): Promise<IDeleteRatesResponse> {
    return serverAxios
      .delete(`/${this.route}`, {
        data: prepareFiltersOfSelection(filters),
      })
      .then((response: AxiosResponse<IDeleteRatesResponse>) => {
        return response.data;
      });
  }
}

export default RatesHttp;
